import { Banner, Button } from 'ui'
import { getLogoutUrl } from '../../../helpers/authConfig'

/** Shape returned by POST /profile (utilities/dashboardFunctions/userProfile.js). */
export interface UserProfile {
    cognitoId: string
    email: string
    emailVerified: boolean
    name: string
    phone: string
    phoneVerified: boolean
    organisation: string
    organisations: string[]
    admin: boolean
    enabled: boolean
    status: string | null
    createdAt: string | null
    lastModifiedAt: string | null
    lastLogin: string | null
    passwordChangedAt: string | null
    dashboardUserId: number | null
    mfa: {
        enabled: boolean
        methods: string[]
        preferred: string | null
        enabledAt: string | null
    }
    notifications: NotificationPreferences
    pendingEmailChange: { newEmail: string; expiresAt: string } | null
}

export interface NotificationPreferences {
    notify_new_sign_in: boolean
    notify_security: boolean
    notify_product_updates: boolean
}

/** One row of POST /profile-sessions. */
export interface DashboardSession {
    id: number
    current: boolean
    browser: string | null
    os: string | null
    device: string | null
    label: string
    ip: string | null
    signedInAt: string | null
    lastActiveAt: string | null
    revokedAt: string | null
    revokedReason: string | null
    active: boolean
}

export const formatDateTime = (value: string | null | undefined): string => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

export const formatDate = (value: string | null | undefined): string => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

/** "just now", "5 min ago", "3 hours ago", else the date. */
export const formatRelative = (value: string | null | undefined): string => {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    const diffMs = Date.now() - date.getTime()
    const minutes = Math.round(diffMs / 60000)
    if (minutes < 1) return 'just now'
    if (minutes < 60) return `${minutes} min ago`
    const hours = Math.round(minutes / 60)
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
    const days = Math.round(hours / 24)
    if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`
    return formatDate(value)
}

/**
 * Email with a soft break opportunity before the `@`, so very long addresses
 * that must wrap split local-part / domain rather than at an arbitrary letter.
 */
export const BreakableEmail = ({ email }: { email: string }) => {
    const at = email.lastIndexOf('@')
    if (at <= 0) return <>{email}</>
    return (
        <>
            {email.slice(0, at)}
            <wbr />
            {email.slice(at)}
        </>
    )
}

export const apiErrorMessage = (error: any, fallback: string): string =>
    error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback

export const apiErrorCode = (error: any): string | undefined => error?.response?.data?.code

/** Clear the local session (keeping UI prefs) and go through Cognito logout. */
export const signOut = () => {
    const collapsedPref = window.localStorage.getItem('sidebarCollapsed')
    window.localStorage.clear()
    if (collapsedPref) window.localStorage.setItem('sidebarCollapsed', collapsedPref)
    window.location.href = getLogoutUrl()
}

/**
 * Shown when the API answers REAUTH_REQUIRED: the session's token predates the
 * scope that self-service Cognito calls need, so the only fix is a fresh login.
 */
export const ReauthBanner = ({ action }: { action: string }) => (
    <Banner
        tone="warn"
        title="Please sign in again"
        actions={
            <Button size="sm" onClick={signOut}>
                Log out
            </Button>
        }
    >
        Your current session was started before {action} was enabled. Log out and back in, then try again.
    </Banner>
)
